/**
 * ATL-1445 stage 3 — the `writeW` seam and the session write-mode pin (P4), with the contract of the
 * backend-first branch (P9).
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §1 (the seam), §4 (reading the flag at
 * startup), §11 (P4 as built), §17 (P9 as built).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE THREE MODES
 *
 *  - FIRESTORE (every session today): `return op.firestoreFirst()` — the SAME promise object, with no
 *    await, wrapper or extra tick in between, and NOTHING else read off the operation (not even its
 *    plan). A converted writer is byte-identical to the code it was, because its `firestoreFirst` IS
 *    that code, verbatim (P9 decision 1, §17).
 *  - REFUSE (§4's invalid state; decision S1 of #929; an unpinned capable bundle, or an unpinned STAGING
 *    cut-over preview build — P9b review 1, ATL-1678): rejects with
 *    `WriteWRefusedError` and the body does not run. The cashier is told why through the notice sink
 *    (`installWriteWNotices`: S2's required "visible notice", owned by P9).
 *  - SUPABASE (a capable bundle on a Supabase-master organization with Supabase reads — unreachable in
 *    production while the compiled-in manifest shows an unconverted W writer): the installed
 *    backend-first branch (`writeWBackendFirst.ts`) runs `op.plan()` and puts the write in outbox v2.
 *    With no branch installed, or a seam that declares none, it rejects
 *    (`WriteWBackendFirstUnavailableError`). It NEVER falls back to Firestore-first.
 *
 * WHY THE PLAN IS LAZY (P9 decision 1). §1 had `planX` run before the seam. A plan builds the request
 * (the dual-write's DTO), the local effects and the ids — and today's writers build their DTO AFTER
 * the Firestore commit, where a DTO that cannot be built throws after the sale is booked and the
 * Sentry warnings of `coerceCartItemForApi` follow the commit. Building it ahead of the seam would move
 * those in every session, for a branch no session can take. So the plan is a function only Supabase
 * mode calls: Firestore-mode identity holds by construction, and request parity is proved by test
 * (each converted writer's Supabase-mode request equals the request its own Firestore-mode dual-write
 * sends, for the same input).
 *
 * ⚠️ The pinned mode binds only writers that CALL `writeW`. In any incapable bundle some W writer
 * still writes Firestore-first without asking. Refusing those for a Supabase-master organization needs
 * a production Firestore-write interceptor keyed on this pin (ATL-1601; stage-3 gate
 * `incapable-bundle-w-refusal`, required by the setter's enable).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE PIN LIVES HERE
 *
 * The mode is PINNED per session, never re-read per call (§1): re-evaluating the read flag at each
 * call site is what gave ATL-888 its split-brain till, with half the screens on each store. The seam
 * is a plain module that the writers in `global/Firebase/*.ts` call, so the pin cannot be a React ref
 * the way `sessionReadsSupabaseRef` is; it is module state, and it lives in THIS module so the only
 * way to set the mode the seam obeys is through the seam's own export. `AppStack.getInitialData`
 * pins it from the same organization-document read as the read source
 * (`primeOrganizationForReadCutover`), so the two planes of one session are decided together — as
 * soon as the device's memory of the flag has loaded, WITHOUT holding up the first data load (the
 * memory read is not awaited on the startup path).
 *
 * Re-pinning within a process:
 *   - same organization, same decision → nothing changes (`kept`);
 *   - same organization, a DIFFERENT decision (the flag moved since startup, or a later run read it
 *     where the first could not) → the pin is KEPT and the divergence reported (`kept-divergent`).
 *     Changing a session's write mode is a reload (P5's watcher, with v2's drain), never a
 *     mid-session switch;
 *   - another organization (the user signed into a different one) → the pin is replaced.
 *
 * Before any pin (a write fired before the memory loaded), an incapable bundle is in Firestore
 * mode — exactly today's behaviour, and what its unconverted writers do regardless. A capable bundle
 * would REFUSE until pinned: it cannot know whether Firestore-first is a second master.
 */
import type { JsonValue, OutboxRequest, OutboxWriteKind } from './outboxEntry'
import { WRITE_W_CAPABILITY, WRITE_W_SEAM, type WriteWCapability } from './writeWCapability'
import {
  decideSessionWriteMode,
  outboxV2BlocksFirestore,
  resolveOrgWritesMaster,
  type LastKnownWritesMaster,
  type OutboxV2Presence,
  type SessionWriteModeDecision,
  type WriteMode,
  type WriteModeReason,
  type WritesMasterObservation,
} from './writeMode'
import { writesMasterMemory, type WritesMasterMemory } from './writesMasterMemory'

// ============================================================================
// The operation
// ============================================================================

/**
 * What a converted writer's PLAN returns (§1 `planX`; P9 decision 1: called only in Supabase mode). It
 * mints the device ids, runs today's validation, and builds the request today's dual-write sends and
 * the local effects, from the same code the Firestore body uses. It writes nothing: no Firestore write
 * (the census holds every one of those inside `firestoreFirst`) and no backend send (its request is
 * CAPTURED by `buildOutboxRequest`, never sent — the census recognises the capture, `wWriterCensus.ts`).
 */
export interface WPlan<R> {
  /** Device-minted Firestore id = the backend's idempotency key (required, invariant 13). */
  documentId: string
  /** Other documents this write references (a sale's client and products, a payment's sale). */
  relatedIds?: readonly string[]
  /** Built once from the DTO today's dual-write sends; persisted as is (no headers). */
  request: OutboxRequest
  /** Local-view effects (§3): stock deltas, dailyStat fields, the sale row, shift status. */
  effects?: JsonValue | null
  /**
   * The writer's return value once the server acknowledged the write (e.g. the sale's document
   * reference). `ack` is the response body, or `undefined` when the acknowledgement was observed
   * without one (another tab delivered the entry). May run the writer's own NON-W tail after the ack
   * (a void's fiscal return and audit record), so it may be async.
   */
  fromAck: (ack: unknown) => R | Promise<R>
  /**
   * REFUSE-OFFLINE writes (P9 review 1, S2-b; REQUIRED for them since review 2, S3-5): what a 2xx answer
   * says about THIS request — `applied` (this request applied the write), `absent` (the document was
   * already gone: someone applied it before — who, the answer does not say), or `unrecognized`. Only an
   * `applied` answer runs `fromAck`; an `unrecognized` one, first or re-sent, is an outcome the writer
   * cannot vouch for (`WriteWOutcomeUnknownError`, no tail). A refuse-offline plan without it is refused
   * before anything is sent. A queued write never reads it.
   */
  ackDisposition?: (ack: unknown) => WAckDisposition
  /**
   * REFUSE-OFFLINE writes (S3-3): the writer's return value when its FIRST send found the document already
   * gone (`ackDisposition` → `absent`, or an entity-absent 404 on a delete). Omitted ⇒ `fromAck`.
   */
  fromAbsent?: (ack: unknown) => R | Promise<R>
  /**
   * QUEUED writes (P9 review 1, S2-c): a side effect outside W that must happen BEFORE the write is
   * persisted but only once every refusal that does not depend on it has passed — a pay-later payment's
   * fiscal receipt. The plan's own `request` and `effects` are built WITHOUT it; the branch checks the pin,
   * the v1 → v2 migration policy and that request's shape (`prepareQueuedWrite`, the store's own rules),
   * then calls this ONCE, immediately before the durable append, and persists the request and effects it
   * returns (which carry the side effect's result). A refuse-offline write never calls it.
   */
  beforeAppend?: () => Promise<{ request: OutboxRequest; effects?: JsonValue | null }>
}

/** What a refuse-offline write's 2xx answer says about the request that got it (`WPlan.ackDisposition`). */
export type WAckDisposition = 'applied' | 'absent' | 'unrecognized'

/**
 * Why a refuse-offline write's outcome is unknown (review 1 S2-b, review 2 S3-5): no definite answer
 * (`unanswered`); the document found already gone after an ambiguous attempt (`unattributable`); or an
 * answer the writer does not recognise (`unrecognized`).
 */
export type WriteWOutcomeUnknownCause = 'unanswered' | 'unattributable' | 'unrecognized'

/** One W write, as a converted writer hands it to the seam (§1). */
export interface WOperation<R> {
  /** The SAME `module.op` label today's dual-write uses (telemetry continuity). */
  label: string
  kind: OutboxWriteKind
  /**
   * Invariant 12: `'refuse'` for the money-moving deletes that fail visibly offline today (they run in
   * a Firestore transaction). In Supabase mode such a write is never queued: it is sent once, only
   * behind an empty queue, and a send that does not land is a visible failure that applied nothing.
   */
  offline: 'queue' | 'refuse'
  /** Today's body, verbatim: the Firestore batch commit plus its best-effort dual-write. */
  firestoreFirst: () => Promise<R>
  /** Supabase mode only (P9 decision 1): never called — never even read — in Firestore mode. */
  plan: () => Promise<WPlan<R>>
}

/** A W write refused because the session is in the `refuse` mode (§4's invalid state, or unpinned). */
export class WriteWRefusedError extends Error {
  readonly code = 'WRITE_W_REFUSED'
  constructor(
    readonly label: string,
    readonly reason: WriteModeReason | 'unpinned',
  ) {
    super(`writeW refused ${label}: ${reason}`)
    this.name = 'WriteWRefusedError'
  }
}

/**
 * Supabase mode reached with no backend-first branch to run it: the seam declares none, or the session
 * never installed one (its drainer did not start). Refused, never Firestore-first.
 */
export class WriteWBackendFirstUnavailableError extends Error {
  readonly code = 'WRITE_W_BACKEND_FIRST_UNAVAILABLE'
  constructor(readonly label: string) {
    super(`writeW has no backend-first branch to run (seam v${WRITE_W_SEAM.version}); refusing ${label}`)
    this.name = 'WriteWBackendFirstUnavailableError'
  }
}

/**
 * P9b, STAGING cut-over preview only (`writeWPreview.ts`): an UNCONVERTED W writer was called in a
 * Supabase-mode session. It has no backend-first path, and writing Firestore-first would be a second
 * master, so it refuses (`assertWWriterAvailable`).
 */
export class WriteWPreviewUnavailableError extends Error {
  readonly code = 'WRITE_W_PREVIEW_UNAVAILABLE'
  constructor(readonly writer: string) {
    super(`${writer} is not converted to writeW yet: unavailable in the write cut-over preview`)
    this.name = 'WriteWPreviewUnavailableError'
  }
}

// ============================================================================
// The session pin
// ============================================================================

export type SessionWriteModePinStatus =
  /** First pin of this process. */
  | 'pinned'
  /** Same organization, same decision: nothing changed. */
  | 'kept'
  /** Same organization, different decision: the earlier pin stands; a reload (P5) applies the new one. */
  | 'kept-divergent'
  /** A different organization: the pin was replaced. */
  | 'replaced-organization'
  /** No organization id: nothing was pinned (writes stay in the unpinned mode). */
  | 'not-pinned'

export interface SessionWriteModePin {
  decision: SessionWriteModeDecision
  pinnedAt: string
}

export interface SessionWriteModePinOutcome {
  status: SessionWriteModePinStatus
  /** The pin in force AFTER this call (null only for `not-pinned` before any pin). */
  pin: SessionWriteModePin | null
  /** The decision this call computed (for `kept-divergent`, the one that was NOT applied). */
  decision: SessionWriteModeDecision | null
  /** The memory write this call started, if any (resolves to whether it was stored; never rejects). */
  remembered: Promise<boolean> | null
}

let pinned: SessionWriteModePin | null = null

/**
 * A deep, frozen copy of a plain JSON-like value (P5). The pin is READ by the live watcher
 * (`writeCutoverWatch.ts`), the outbox queue and telemetry, and only this module may change it: a
 * reader that wrote `pin.decision.mode = …` would switch the session's write mode without a reload,
 * which is the split-brain ATL-888 exists to prevent. Copying first keeps the caller's own inputs
 * (the observation, the recalled memory) unfrozen.
 */
const frozenCopy = <T>(value: T): T => {
  if (Array.isArray(value)) return Object.freeze(value.map((item: unknown) => frozenCopy(item))) as unknown as T
  if (value !== null && typeof value === 'object') {
    const copy: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) copy[key] = frozenCopy(item)
    return Object.freeze(copy) as T
  }
  return value
}

const sameDecision = (a: SessionWriteModeDecision, b: SessionWriteModeDecision): boolean =>
  a.mode === b.mode && a.reason === b.reason && a.resolution.writesMaster === b.resolution.writesMaster

/** Apply a decision to the process's pin (the rules in the module docblock). */
export const pinSessionWriteMode = (
  decision: SessionWriteModeDecision,
  now: Date = new Date(),
): Omit<SessionWriteModePinOutcome, 'remembered'> => {
  // Frozen (P5): nothing outside this function may change the mode the seam obeys.
  const next: SessionWriteModePin = frozenCopy({ decision, pinnedAt: now.toISOString() })
  // When the decision is applied, the outcome reports the pin's own (frozen) decision: one identity.
  if (!pinned) {
    pinned = next
    return { status: 'pinned', pin: pinned, decision: next.decision }
  }
  if (pinned.decision.organizationId !== decision.organizationId) {
    pinned = next
    return { status: 'replaced-organization', pin: pinned, decision: next.decision }
  }
  return { status: sameDecision(pinned.decision, decision) ? 'kept' : 'kept-divergent', pin: pinned, decision }
}

export interface StartupWriteModeInput {
  /** The signed-in organization's Firestore id. */
  organizationId: string | null | undefined
  /** `primeOrganizationForReadCutover(...).writesMaster` — the same read that primed the read flag. */
  observation: WritesMasterObservation
  /** `writesMasterMemory.recall(organizationId)`, started before that read. */
  lastKnown: LastKnownWritesMaster
  /** What this session chose for READS (`sessionReadsSupabaseRef.current`). */
  sessionReadsSupabase: boolean
  /**
   * P5: what startup found in outbox v2 for this organization (`probeOutboxV2Presence`), when it had
   * to look (`shouldProbeOutboxV2AtStartup`). Omitted ⇒ `not-checked`: P4's behaviour exactly.
   */
  outboxV2?: OutboxV2Presence
  now?: Date
  /** Injected for tests; the app uses the compiled-in constant and AsyncStorage. */
  capability?: WriteWCapability
  memory?: WritesMasterMemory
}

/**
 * Resolve the organization's write master, decide this session's write mode, pin it, and remember a
 * newly confirmed master. Called once per `getInitialData` run. Never throws, never awaits: the pin
 * is in force when it returns; the memory write proceeds in the background (`remembered`).
 */
export const pinSessionWriteModeAtStartup = ({
  organizationId,
  observation,
  lastKnown,
  sessionReadsSupabase,
  outboxV2 = 'not-checked',
  now = new Date(),
  capability = WRITE_W_CAPABILITY,
  memory = writesMasterMemory,
}: StartupWriteModeInput): SessionWriteModePinOutcome => {
  if (!organizationId) return { status: 'not-pinned', pin: pinned, decision: null, remembered: null }
  const resolution = resolveOrgWritesMaster(observation, lastKnown)
  const decision = decideSessionWriteMode({ organizationId, resolution, sessionReadsSupabase, capability, outboxV2 })
  const outcome = pinSessionWriteMode(decision, now)
  // Remembered even when the pin is kept: the observation is still the truth, and the NEXT session
  // must start from it. Only `resolution.remember` is written — never a value derived from memory.
  // P5: a server 'firestore' does NOT lower the memory while outbox v2 still holds master writes:
  // the memory is what a later unreadable start falls back on, and it must not say Firestore-first
  // while there is something to deliver first. The drain-to-empty rollback lowers it once v2 is empty.
  const toRemember = resolution.remember === 'firestore' && outboxV2BlocksFirestore(outboxV2) ? null : resolution.remember
  const remembered = toRemember ? memory.remember(organizationId, toRemember, now) : null
  return { ...outcome, remembered }
}

/** The pin in force, or null before the first one. */
export const getSessionWriteModePin = (): SessionWriteModePin | null => pinned

/**
 * The mode `writeW` obeys right now: the pin's, or the unpinned default (module docblock).
 *
 * Unpinned, a bundle that can go backend-first REFUSES: it cannot know whether Firestore-first would be a
 * second master until the organization's write master is read. That is a capable bundle, and — P9b
 * review 1 (ATL-1678) — a STAGING cut-over preview build too (`WRITE_W_CAPABILITY.preview`, compiled to
 * false in every production build): on a slot organization the flip script made Supabase-master, a
 * Firestore-first write before the pin landed (or for the whole session, when the pin never lands —
 * AppStack only logs that failure) would be a second master. Every other bundle stays in Firestore mode,
 * which is what its writers do anyway.
 */
export const currentSessionWriteMode = (): { mode: WriteMode; reason: WriteModeReason | 'unpinned' } => {
  if (pinned) return { mode: pinned.decision.mode, reason: pinned.decision.reason }
  return WRITE_W_CAPABILITY.capable || WRITE_W_CAPABILITY.preview === true
    ? { mode: 'refuse', reason: 'unpinned' }
    : { mode: 'firestore', reason: 'unpinned' }
}

/** Test seam: forget the pin (a new JS process). */
export const resetSessionWriteModeForTests = (): void => {
  pinned = null
}

// ============================================================================
// The seam
// ============================================================================

/**
 * The backend-first branch (P9, `writeWBackendFirst.ts`): what Supabase mode does with an operation.
 * It is INSTALLED rather than imported, so this module stays a pure pass-through in every session
 * (its imports are pinned by `writeW.test.js`) and no outbox module is evaluated for a write in
 * Firestore mode. The session installs it when it pins Supabase mode (`writeCutoverSession.ts`).
 */
export interface WriteWBackendFirstBranch {
  /** Never throws synchronously; the returned promise settles as the write does (§1, steps 1–5). */
  run<R>(op: WOperation<R>, pin: SessionWriteModePin): Promise<R>
}

let backendFirstBranch: WriteWBackendFirstBranch | null = null

/** Install (or, with `null`, remove) the backend-first branch. */
export const installWriteWBackendFirst = (branch: WriteWBackendFirstBranch | null): void => {
  backendFirstBranch = branch
}

/**
 * What the seam tells the person at the till:
 *  - `refused`: the session refuses W writes (S2's required "visible notice", §11), with the reason;
 *  - `syncing`: a write waits for (or, Q-P8-5 `'refuse'`, is refused during) the v1 → v2 migration;
 *  - `not-sent`: a refuse-offline write (invariant 12) could not be sent behind an empty queue — `why`
 *    `queue-held` (review 1, S2-a) when a write that needs a person stops the queue, not connectivity;
 *  - `outcome-unknown` (review 1, S2-b): a refuse-offline write was sent, may have applied, and could not
 *    be confirmed — `cause` says why (`WriteWOutcomeUnknownCause`);
 *  - `maybe-queued` (review 1, S3-5): a queued write's append has an unknown outcome — it may be queued and
 *    must NOT be repeated (a repeat is a second write).
 */
export type WriteWNotice =
  | { kind: 'refused'; label: string; reason: WriteModeReason | 'unpinned' }
  | { kind: 'syncing'; label: string }
  | { kind: 'not-sent'; label: string; why: string }
  | { kind: 'outcome-unknown'; label: string; cause: WriteWOutcomeUnknownCause }
  | { kind: 'maybe-queued'; label: string }
  /** P9b, staging cut-over preview only: an UNCONVERTED W writer refused in Supabase mode. */
  | { kind: 'preview-unavailable'; label: string }

let noticeSink: ((notice: WriteWNotice) => void) | null = null

/** Install (or, with `null`, remove) where the seam's notices go (the app's toast). */
export const installWriteWNotices = (sink: ((notice: WriteWNotice) => void) | null): void => {
  noticeSink = sink
}

/** Tell the till (the backend-first branch's notices go through the same sink). Never throws. */
export const notifyWriteW = (notice: WriteWNotice): void => notify(notice)

const notify = (notice: WriteWNotice): void => {
  try {
    noticeSink?.(notice)
  } catch {
    // A notice that fails to display never changes what the write does.
  }
}

/** Test seam: a new JS process (no branch, no notice sink). */
export const resetWriteWInstallsForTests = (): void => {
  backendFirstBranch = null
  noticeSink = null
}

/**
 * Perform one W write in the session's write mode.
 *
 *  - Firestore mode: `return op.firestoreFirst()` — the same promise, nothing else read or run.
 *    `plan` is not touched, no telemetry is emitted, no storage is written: pass-through is
 *    byte-identical to calling the body directly (the contract test runs this for every W writer in
 *    the registry, in every resolution state).
 *  - Refuse mode: rejects with `WriteWRefusedError`, and tells the till why (the notice sink); the body
 *    does not run.
 *  - Supabase mode: the installed backend-first branch (P9). Without one — or while the seam declares
 *    no backend-first branch — it rejects with `WriteWBackendFirstUnavailableError`. It never runs the
 *    Firestore body.
 *
 * Deliberately not `async`: an async wrapper would return a NEW promise one tick later, and a
 * synchronous throw from the body would change from a throw into a rejection. Today's writers call
 * their body directly; the seam must be indistinguishable from that.
 */
export function writeW<R>(op: WOperation<R>): Promise<R> {
  const { mode, reason } = currentSessionWriteMode()
  if (mode === 'firestore') return op.firestoreFirst()
  if (mode === 'refuse') {
    notify({ kind: 'refused', label: op.label, reason })
    return Promise.reject(new WriteWRefusedError(op.label, reason))
  }
  const branch = backendFirstBranch
  const pin = pinned
  if (!WRITE_W_SEAM.backendFirst || !branch || !pin) return Promise.reject(new WriteWBackendFirstUnavailableError(op.label))
  return branch.run(op, pin)
}

/**
 * P9b — THE PREVIEW GUARD of an UNCONVERTED W writer (design §18). Every W writer the registry still
 * marks unconverted calls this as the FIRST statement of its body, with its own registry key
 * (`<file>#<function>`); a no-Firestore writer calls it first in each nested function that reaches its
 * W route. `previewGateProblems` (`scripts/wWriterCensus.ts`) holds all of that, and holds that no
 * converted writer — and no other function — calls it.
 *
 *  - Any build that is not a STAGING cut-over preview (every production build, where
 *    `WRITE_W_CAPABILITY.preview` is compiled to false — `writeWPreview.ts`): returns at once. Nothing is
 *    read, written or awaited: the writer runs exactly as before.
 *  - A preview build in Firestore mode (every Firestore-master organization): returns.
 *  - A preview build in refuse mode: refuses as `writeW` would (`refused` notice, `WriteWRefusedError`).
 *  - A preview build in Supabase mode: the writer has no backend-first path, and Firestore-first would
 *    be a second master — `preview-unavailable` notice, `WriteWPreviewUnavailableError`.
 *
 * It THROWS (synchronously) rather than returning a rejection: every caller is the first statement of an
 * `async` body, where a throw is the rejection and nothing after it runs. Deliberately not a substitute
 * for ATL-1601: production builds skip it, and a Firestore write outside the registry never meets it.
 */
export function assertWWriterAvailable(writer: string): void {
  if (WRITE_W_CAPABILITY.preview !== true) return
  const { mode, reason } = currentSessionWriteMode()
  if (mode === 'firestore') return
  if (mode === 'refuse') {
    notify({ kind: 'refused', label: writer, reason })
    throw new WriteWRefusedError(writer, reason)
  }
  notify({ kind: 'preview-unavailable', label: writer })
  throw new WriteWPreviewUnavailableError(writer)
}

/**
 * The rejection handler for a FIRE-AND-FORGET call of a W writer (`writer(…).catch(rethrowUnlessWRefusal)`,
 * P9b review 1): a call nothing awaits, so a refusal would otherwise end as an unhandled rejection.
 *
 * It ends exactly the two W refusals — `WriteWRefusedError` and `WriteWPreviewUnavailableError` — and only
 * because both were already surfaced when they were raised: `writeW` and `assertWWriterAvailable` notify
 * (`refused` / `preview-unavailable`, toasted by the session's notice sink) before they throw. Every other
 * error is rethrown unchanged, so it stays the unhandled rejection it was before the handler was attached
 * (the global handler, Sentry), and a Firestore-mode session — where neither refusal can be raised — sees
 * no change at all. Attaching it does not change when the writer starts or what it awaits.
 */
export const rethrowUnlessWRefusal = (error: unknown): void => {
  if (error instanceof WriteWRefusedError || error instanceof WriteWPreviewUnavailableError) return
  throw error
}
